import React from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';

// In the order of what interrupting costs the merchant: words already paid for, a save cut
// off halfway, edits never sent. Null when nothing is running - the dialog then says so.
export const guardReason = ({ translating, saving, unsaved }) => {
  if(translating) return 'translating';
  if(saving) return 'saving';
  if(unsaved) return 'unsaved';
  return null;
}

// One dialog for every way out of the current work: switching language or theme, leaving
// the page. `reason` is read on every render, so work that finishes while the merchant is
// reading turns the warning into an all-clear instead of closing it under them. Only that
// one change is shown: a translation hands over to its save, and flashing the save's
// warning in between would read as a third, unrelated problem.
const ContextGuardDialog = ({ open, reason, action, onCancel, onConfirm }) => {
  const { t } = useTranslation();
  const shownReason = React.useRef(null);
  if(!open) {
    shownReason.current = null;
    return null;
  }
  if(reason && !shownReason.current) {
    shownReason.current = reason;
  }

  const finished = reason === null;
  const key = finished ? 'finished' : shownReason.current;
  const confirmKey = finished ? action : `${action}Anyway`;

  return (
    <Dialog
      open={true}
      onClose={onCancel}
      aria-labelledby="context-guard-title"
      aria-describedby="context-guard-description"
    >
      <DialogTitle id="context-guard-title">{t(`contextGuard.${key}Title`)}</DialogTitle>
      <DialogContent>
        <DialogContentText id="context-guard-description">
          {t(`contextGuard.${key}Description`)}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} variant="outlined">
          {t('system.cancel')}
        </Button>
        <Button onClick={onConfirm} color="primary" variant="contained" autoFocus>
          {t(`contextGuard.${confirmKey}`)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ContextGuardDialog;
